import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, ConfirmDialog, EmptyState, Spinner, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { auth as authApi } from '../../lib/api';
import { useT } from '../../i18n';

/** Turns a raw user-agent string into "Chrome on macOS"-style label. Best effort only. */
function describeDevice(userAgent) {
  if (!userAgent) return null;
  const ua = userAgent;
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : null;
  const os =
    /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Macintosh/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser || os;
}

/**
 * My Account card listing every device with an active session (one per refresh token),
 * with per-device sign-out and a "sign out everywhere else" action.
 */
export default function SessionsCard() {
  const t = useT();
  const toast = useToast();
  const [sessions, setSessions] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [confirmOthers, setConfirmOthers] = useState(false);
  const [othersBusy, setOthersBusy] = useState(false);

  const load = useCallback(() => {
    authApi.listSessions().then(setSessions).catch(() => setSessions([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const revokeOne = async () => {
    setRevokeBusy(true);
    try {
      await authApi.revokeSession(revoking.id);
      toast.success(t('Device signed out'));
      setRevoking(null);
      load();
    } catch (err) {
      toast.error(t('Could not sign the device out'), { description: err?.message });
    } finally {
      setRevokeBusy(false);
    }
  };

  const revokeOthers = async () => {
    setOthersBusy(true);
    try {
      const res = await authApi.revokeOtherSessions();
      toast.success(t('{count} other device(s) signed out', { count: res?.revoked ?? 0 }));
      setConfirmOthers(false);
      load();
    } catch (err) {
      toast.error(t('Could not sign other devices out'), { description: err?.message });
    } finally {
      setOthersBusy(false);
    }
  };

  const others = (sessions || []).filter((s) => !s.isCurrent);

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('Devices & sessions')}</Card.Title>
        <Card.Subtitle>{t("Everywhere you're signed in right now. Sign out anything you don't recognize.")}</Card.Subtitle>
      </Card.Header>
      <Card.Body>
        {sessions === null ? (
          <Spinner />
        ) : sessions.length === 0 ? (
          <EmptyState icon={<Icon.MonitorSmartphone size={26} />} title={t('No active sessions')} />
        ) : (
          <ul className="acct-sessions">
            {sessions.map((s) => (
              <li className="acct-sessions__row" key={s.id}>
                <span className="acct-passkeys__icon"><Icon.MonitorSmartphone size={18} /></span>
                <span className="acct-passkeys__info">
                  <span className="acct-passkeys__name">
                    {describeDevice(s.userAgent) || t('Unknown device')}
                    {s.isCurrent && <Badge tone="success">{t('This device')}</Badge>}
                  </span>
                  <span className="acct-form__muted">
                    {s.ipAddress ? `${s.ipAddress} · ` : ''}
                    {t('Signed in {date}', { date: new Date(s.signedInAt).toLocaleString() })}
                    {' · '}
                    {t('Last active {date}', { date: new Date(s.lastActiveAt).toLocaleString() })}
                  </span>
                </span>
                {!s.isCurrent && (
                  <Button variant="ghost" size="sm" onClick={() => setRevoking(s)}>
                    <Icon.LogOut size={14} />
                    {t('Sign out')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {others.length > 0 && (
          <div className="acct-form__actions">
            <Button variant="secondary" onClick={() => setConfirmOthers(true)}>
              {t('Sign out all other devices')}
            </Button>
          </div>
        )}
      </Card.Body>

      <ConfirmDialog
        open={!!revoking}
        onCancel={() => setRevoking(null)}
        onConfirm={revokeOne}
        loading={revokeBusy}
        tone="danger"
        title={t('Sign this device out?')}
        description={t('It will need to sign in again to use the system.')}
        confirmLabel={t('Sign out')}
      />
      <ConfirmDialog
        open={confirmOthers}
        onCancel={() => setConfirmOthers(false)}
        onConfirm={revokeOthers}
        loading={othersBusy}
        tone="danger"
        title={t('Sign out all other devices?')}
        description={t('Every session except this one will be ended immediately.')}
        confirmLabel={t('Sign out others')}
      />
    </Card>
  );
}
